import { Home, ChevronRight, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBreadcrumb } from '@/features/sources/hooks/useFolders';
import type { BreadcrumbItem } from '@/features/sources/types';

interface BreadcrumbNavProps {
  folderId: string | undefined;
  onNavigate: (folderId: string | null) => void;
  className?: string;
}

interface BreadcrumbSegmentProps {
  item: BreadcrumbItem;
  isLast: boolean;
  onNavigate: (folderId: string) => void;
}

function BreadcrumbSegment({ item, isLast, onNavigate }: BreadcrumbSegmentProps) {
  if (isLast) {
    return (
      <li className="flex items-center gap-1.5">
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
          <FolderOpen className="h-3.5 w-3.5 text-indigo-600" />
          <span className="truncate max-w-[200px]">{item.name}</span>
        </span>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-1.5">
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      <button
        type="button"
        onClick={() => onNavigate(item.id)}
        className="flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-indigo-600"
      >
        <span className="truncate max-w-[200px]">{item.name}</span>
      </button>
    </li>
  );
}

export function BreadcrumbNav({ folderId, onNavigate, className }: BreadcrumbNavProps) {
  const { data: breadcrumbs, isLoading } = useBreadcrumb(folderId);

  if (isLoading) {
    return (
      <nav className={cn('flex items-center gap-1', className)}>
        <div className="h-5 w-5 animate-pulse rounded bg-slate-100" />
        <div className="h-4 w-4 animate-pulse rounded bg-slate-100" />
        <div className="h-5 w-24 animate-pulse rounded bg-slate-100" />
      </nav>
    );
  }

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;

  return (
    <nav aria-label="Breadcrumb" className={cn('flex items-center', className)}>
      <ol className="flex items-center gap-1 overflow-hidden">
        <li>
          <button
            type="button"
            onClick={() => onNavigate(null)}
            className={cn(
              'flex items-center gap-1.5 text-sm transition-colors',
              hasBreadcrumbs
                ? 'text-slate-500 hover:text-indigo-600'
                : 'text-slate-900 font-medium'
            )}
          >
            <Home className={cn(
              'h-4 w-4 shrink-0',
              hasBreadcrumbs ? 'text-slate-400' : 'text-indigo-600'
            )} />
            <span>All Sources</span>
          </button>
        </li>

        {hasBreadcrumbs &&
          breadcrumbs.map((item, index) => (
            <BreadcrumbSegment
              key={item.id}
              item={item}
              isLast={index === breadcrumbs.length - 1}
              onNavigate={onNavigate}
            />
          ))}
      </ol>
    </nav>
  );
}
